import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Alert, Card, Row, Col, Spinner } from 'react-bootstrap';
import {
  useAdminTableColumn,
  useCreateAdminTableColumn,
  useUpdateAdminTableColumn,
  useDeleteAdminTableColumn
} from 'api/hooks/use-admin-table-columns';
import { useAdminSections } from 'api/hooks/use-admin-sections';

const DISPLAY_AS_OPTIONS = [
  'account_segment',
  'boolean',
  'date',
  'flag_count',
  'link',
  'money',
  'percent',
  'risk_score',
  'status',
  'text',
  'time_in_queue',
  'two_digit_money'
];

const GRAIN_OPTIONS = ['header', 'line_item', 'audit_log'];
const ROLE_OPTIONS = ['measure', 'dimension', 'other'];
const AGGREGATION_OPTIONS = ['sum', 'avg', 'count', 'min', 'max', 'distinct_count'];

const FILTER_TYPE_OPTIONS = [
  'basic',
  'boolean',
  'date',
  'greater_than',
  'group_multiselect',
  'mcc_select',
  'min_max',
  'min_max_percent',
  'multiselect',
  'reference',
  'select',
  'time_in_seconds'
];

const TableColumnForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: tableColumn, isLoading } = useAdminTableColumn({ id });
  const { data: sections } = useAdminSections();
  const createMutation = useCreateAdminTableColumn({
    options: {
      onSuccess: () => navigate('/admin/table-columns')
    }
  });
  const updateMutation = useUpdateAdminTableColumn({
    id,
    options: {
      onSuccess: () => navigate('/admin/table-columns')
    }
  });
  const deleteMutation = useDeleteAdminTableColumn({
    options: {
      onSuccess: () => navigate('/admin/table-columns')
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    database_column_name: '',
    section_id: '',
    description: '',
    filter_type: '',
    accessor_key: '',
    is_default: false,
    is_sortable: false,
    is_selectable: false,
    display_as: '',
    grouping: '',
    grain: '',
    role: '',
    default_aggregation: '',
    source_association: '',
    source_attribute: ''
  });

  useEffect(() => {
    if (tableColumn) {
      setFormData({
        name: tableColumn.name || '',
        database_column_name: tableColumn.database_column_name || '',
        section_id: tableColumn.section_id || '',
        description: tableColumn.description || '',
        filter_type: tableColumn.filter_type || '',
        accessor_key: tableColumn.accessor_key || '',
        is_default: tableColumn.is_default || false,
        is_sortable: tableColumn.is_sortable || false,
        is_selectable: tableColumn.is_selectable || false,
        display_as: tableColumn.display_as || '',
        grouping: tableColumn.grouping || '',
        grain: tableColumn.grain || '',
        role: tableColumn.role || '',
        default_aggregation: tableColumn.default_aggregation || '',
        source_association: tableColumn.source_association || '',
        source_attribute: tableColumn.source_attribute || ''
      });
    }
  }, [tableColumn]);

  // Only a measure takes a default aggregation; the stored role wins over the derived one.
  const isMeasure = (formData.role || tableColumn?.effective_role) === 'measure';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    // Convert empty section_id to null
    if (submitData.section_id === '') {
      submitData.section_id = null;
    }

    if (isEditing) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this table column? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const error = createMutation.error || updateMutation.error || deleteMutation.error;
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;

  if (isLoading && isEditing) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <h2>{isEditing ? 'Edit Table Column' : 'Create Table Column'}</h2>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error.message || 'An error occurred'}
        </Alert>
      )}

      <Card className="mt-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Database Column Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="database_column_name"
                    value={formData.database_column_name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Section</Form.Label>
                  <Form.Select
                    name="section_id"
                    value={formData.section_id}
                    onChange={handleChange}
                  >
                    <option value="">Global (No Section)</option>
                    {sections?.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.name} ({section.abbreviation})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Accessor Key</Form.Label>
                  <Form.Control
                    type="text"
                    name="accessor_key"
                    value={formData.accessor_key}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Display As</Form.Label>
                  <Form.Select
                    name="display_as"
                    value={formData.display_as}
                    onChange={handleChange}
                  >
                    <option value="">Select...</option>
                    {DISPLAY_AS_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Filter Type</Form.Label>
                  <Form.Select
                    name="filter_type"
                    value={formData.filter_type}
                    onChange={handleChange}
                  >
                    <option value="">Select...</option>
                    {FILTER_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Grouping</Form.Label>
                  <Form.Control
                    type="text"
                    name="grouping"
                    value={formData.grouping}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* How the AI and the chart editor use the column. Blank means "derive from the type". */}
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Grain</Form.Label>
                  <Form.Select name="grain" value={formData.grain} onChange={handleChange}>
                    <option value="">Same as its module</option>
                    {GRAIN_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select name="role" value={formData.role} onChange={handleChange}>
                    <option value="">
                      {tableColumn?.effective_role ? `Derived: ${tableColumn.effective_role}` : 'Derive from type'}
                    </option>
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Default Aggregation</Form.Label>
                  <Form.Select
                    name="default_aggregation"
                    value={formData.default_aggregation}
                    onChange={handleChange}
                    disabled={!isMeasure}
                  >
                    <option value="">
                      {isMeasure && tableColumn?.effective_default_aggregation
                        ? `Derived: ${tableColumn.effective_default_aggregation}`
                        : 'Measures only'}
                    </option>
                    {AGGREGATION_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* A column can read its value from a record the transaction belongs to
                instead of from the section's own detail table — e.g. Vendor Type on
                the AP module reads vendors.general_type. Leave both blank for an
                ordinary detail column. See ColumnSource on the API side; the
                association must be one it allowlists. */}
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Source Association</Form.Label>
                  <Form.Select
                    name="source_association"
                    value={formData.source_association}
                    onChange={handleChange}
                  >
                    <option value="">Detail table (default)</option>
                    <option value="vendor">Vendor</option>
                    <option value="employee">Employee</option>
                    <option value="vendor_master">Vendor Master</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Read this column from an associated record instead of the section&apos;s
                    detail table.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Source Attribute</Form.Label>
                  <Form.Control
                    type="text"
                    name="source_attribute"
                    value={formData.source_attribute}
                    onChange={handleChange}
                    disabled={!formData.source_association}
                    placeholder="e.g. general_type"
                  />
                  <Form.Text className="text-muted">
                    Column on the source table. Defaults to the database column name.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="is_default"
                    label="Is Default"
                    checked={formData.is_default}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="is_sortable"
                    label="Is Sortable"
                    checked={formData.is_sortable}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="is_selectable"
                    label="Is Selectable"
                    checked={formData.is_selectable}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-between mt-4">
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                  className="me-2"
                >
                  {isSubmitting ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
                </Button>
                <Button
                  type="button"
                  variant="light-primary"
                  onClick={() => navigate('/admin/table-columns')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
              {isEditing && (
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  Delete Table Column
                </Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default TableColumnForm;
